/**
 * Unit conversion constants and functions for hydraulic calculations.
 *
 * All conversion functions normalize to SI units:
 *   Length → meters (m)
 *   Pressure → Pascals (Pa)
 *   Flow → kg/s (mass) or m³/s (volumetric)
 *   Density → kg/m³
 *   Viscosity → Pa·s
 *
 * @module shared/hydraulic/physics/unit-conversions
 */

// ============================================================================
// Conversion Factor Constants
// ============================================================================

// Length
export const FT_TO_M = 0.3048;
export const IN_TO_M = 0.0254;
export const MM_TO_M = 0.001;
export const CM_TO_M = 0.01;

// Pressure
// PIPE-FLO classic's own factor, not a rounding of it: ESI_SHARED_LIBRARIES
// libraries/unit_set/trunk/unit_set_tests/abs_gage_unit.cpp:20 asserts psi <-> 6894.757,
// and libraries/valve_calcs/trunk/valve_calcs/src/Cv_from_flow_dP_rho.cpp:29 computes
// with it. We held 6894.76, which put every shared consumer 4.4e-7 off the reference
// — and #850 was about to move two more dialogs onto the divergent value.
export const PSI_TO_PA = 6894.757;

/**
 * Inches of water column → Pa: conventional water column, ρ = 1000 kg/m³
 * exactly (249.08891…). CONFIRMED against PIPE-FLO desktop empirically:
 * ½ρv²/(p_total − p_static) across 26 solved CRAC duct rows → mean 249.058,
 * range [248.958, 249.173] — the 60 °F convention (248.84) lies below the
 * minimum of all 26 and is ruled out
 * (docs/prd/2026-06-12-duct-fan-support.md §2.4, gate R1).
 */
export const IN_H2O_TO_PA = 1000 * 9.80665 * 0.0254;
export const BAR_TO_PA = 1e5;
export const KPA_TO_PA = 1e3;
export const ATM_TO_PA = 101325;
/** 1 mmHg (torr) = 133.322 Pa — the Yaws Type-3 vapor-pressure unit in the fluid database. */
export const MMHG_TO_PA = 133.322;

// Flow (volumetric)
export const GPM_TO_M3S = 6.30902e-5;
// classic: SI/units.cpp:124 documents 1.6666666667e-5 (litres per minute).
export const LPM_TO_M3S = 1.6666666667e-5;
export const LS_TO_M3S = 0.001;
export const FT3_PER_MIN_TO_M3S = 0.0283168 / 60; // cfm → m³/s

// Standard reference conditions for gas density.
// CONFIRMED from ESI C++ (ESI_SHARED_LIBRARIES units/utility/conversion.hpp:
// `standard_reference_pressure` = 101325 Pa, `standard_reference_temperature`
// = (60 + 459.67) / 1.8 K = 60 °F). The air reference density at these
// conditions is 1.22268707852639 kg/m³ (fluid_mechanics/constants/private_/
// air_density.hpp:5); standard densities for other gases follow from the
// actual gas MW via the ideal-gas law (ESI scales the air constant by
// specific gravity, which is the same thing).
export const P_STD_PA = 101325;
export const T_STD_K = (60 + 459.67) / 1.8; // 288.7056 K = 60 °F (ESI exact form)

/** Universal gas constant, J/(mol·K). */
const R_UNIVERSAL_J_MOL_K = 8.314462618;

// Density
export const LBFT3_TO_KGM3 = 16.01846;
export const GCM3_TO_KGM3 = 1000;

// Viscosity
export const CP_TO_PAS = 0.001;

// Temperature offsets
export const FAHRENHEIT_OFFSET = 32;
export const CELSIUS_TO_KELVIN_OFFSET = 273.15;

// Velocity
export const FTS_TO_MS = 0.3048;

// Power
export const HP_TO_W = 745.7;
export const KW_TO_W = 1000;
/** 1 BTU/h = 0.29307107 W. */
export const BTU_H_TO_W = 0.29307107;

// Specific heat
export const BTU_LB_DEGF_TO_J_KG_K = 4186.8;
export const KJ_KG_K_TO_J_KG_K = 1000;

// Mass flow
export const LBS_TO_KGS = 0.453592;

// Reference density (water at ~20°C / 68°F)
export const WATER_DENSITY_KGM3 = 998;

// ============================================================================
// Conversion Functions
// ============================================================================

/** Convert a length value to meters. Defaults to meters for unknown units. */
export function lengthToMeters(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'ft':
    case 'feet':
    case 'foot':
      return value * FT_TO_M;
    case 'in':
    case 'inch':
    case 'inches':
      return value * IN_TO_M;
    case 'm':
    case 'meter':
    case 'meters':
      return value;
    case 'mm':
    case 'millimeter':
    case 'millimeters':
      return value * MM_TO_M;
    case 'cm':
    case 'centimeter':
    case 'centimeters':
      return value * CM_TO_M;
    case 'km':
    case 'kilometer':
    case 'kilometers':
      return value * 1000;
    case 'mi':
    case 'mile':
    case 'miles':
      return value * 1609.344;
    case 'yd':
    case 'yard':
    case 'yards':
      return value * 0.9144;
    default:
      // No silent feet default: Fountain Water District's 1.3 mi trunk main
      // hit this branch and became 0.396 m (mi read as ft) — a frictionless
      // 2.1 km pipe that let the grid loop split drift +2.2 kg/s from
      // desktop. Same masked-import class as the 16x-density bug this
      // file's tests document. Callers wanting a default pass it explicitly
      // (pipe-network-adapter already does `?? 'ft'`).
      throw new Error(`Unknown length unit: '${unit}'`);
  }
}

/**
 * Convert a pressure value to absolute Pascals (SI base).
 *
 * Gauge vs. absolute semantics:
 *   - `psi`, `psia` → treated as absolute (historical default in this codebase)
 *   - `psig`, `psi g` → treated as gauge; atmospheric pressure is ADDED to get absolute
 *   - `kpa`, `kpaa` → treated as absolute
 *   - `kpag`, `kpa g` → gauge
 *   - `bar`, `bara` → absolute
 *   - `barg`, `bar g` → gauge
 *   - `pa`, `atm` → absolute
 *
 * Defaults to psi (absolute) for unknown units.
 */
export function pressureToPa(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'psi':
    case 'psia':
    case 'psi a':
      return value * PSI_TO_PA;
    case 'psig':
    case 'psi g':
      return value * PSI_TO_PA + ATM_TO_PA;
    case 'bar':
    case 'bara':
    case 'bar a':
      return value * BAR_TO_PA;
    case 'barg':
    case 'bar g':
      return value * BAR_TO_PA + ATM_TO_PA;
    case 'kpa':
    case 'kpaa':
    case 'kpa a':
      return value * KPA_TO_PA;
    case 'kpag':
    case 'kpa g':
      return value * KPA_TO_PA + ATM_TO_PA;
    case 'pa':
    case 'paa':
    case 'pa a':
    case 'm^-1 kg s^-2': // pipe-converter internal form
      return value;
    case 'pa g':
    case 'pag':
      return value + ATM_TO_PA;
    case 'atm':
      return value * ATM_TO_PA;
    case 'mmhg':
    case 'torr':
      return value * MMHG_TO_PA;
    case 'inh2o':
    case 'in h2o':
    case 'inwc':
    case 'in wc':
    case 'inh2o a':
    case 'in h2o a':
      return value * IN_H2O_TO_PA;
    case 'inh2o g':
    case 'in h2o g':
      return value * IN_H2O_TO_PA + ATM_TO_PA;
    default:
      return value * PSI_TO_PA; // AFT default: psi
  }
}

/**
 * Unit strings {@link pressureToPa} recognizes. Kept in lockstep with its
 * switch — `pressureToPaStrict` validates against this set so the silent
 * "AFT default: psi" branch can never fire on a strict call site.
 */
const PRESSURE_TO_PA_KNOWN_UNITS = new Set([
  'psi', 'psia', 'psi a', 'psig', 'psi g',
  'bar', 'bara', 'bar a', 'barg', 'bar g',
  'kpa', 'kpaa', 'kpa a', 'kpag', 'kpa g',
  'pa', 'paa', 'pa a', 'pa g', 'pag',
  'm^-1 kg s^-2', // pipe-converter internal form (matches densityToKgM3 / viscosityToPaS)
  'atm', 'mmhg', 'torr',
  'inh2o', 'in h2o', 'inwc', 'in wc',
  'inh2o a', 'in h2o a', 'inh2o g', 'in h2o g',
]);

/**
 * Strict variant of {@link pressureToPa}: identical conversion for the known
 * unit set, but THROWS on anything else instead of silently applying the
 * psi default. Use at ingestion boundaries (e.g. the compressible bridge)
 * where an unknown or empty unit string means corrupted input, not psi.
 *
 * @throws on unrecognized units (see [[feedback_no_silent_fallbacks]]).
 */
const TEMPERATURE_TO_K_KNOWN_UNITS = new Set([
  'degf', '°f', 'f', 'degc', '°c', 'c', 'k', 'kelvin',
]);

/**
 * {@link temperatureToK}, but refusing a unit it does not know.
 *
 * The lenient form falls through to degF for ANY unrecognised unit — an AFT
 * convention that is right for a file format known to be Fahrenheit and wrong
 * for a document a user could have authored. `'MPa'`, a typo, or a unit added
 * upstream all become Fahrenheit silently, which is precisely the shape of
 * failure that hid the 16x density and 1000x viscosity bugs.
 */
export function temperatureToKStrict(value: number, unit: string): number {
  if (!TEMPERATURE_TO_K_KNOWN_UNITS.has(unit.toLowerCase().trim())) {
    throw new Error(
      `temperatureToKStrict: unsupported temperature unit "${unit}" (raw value ${value}). ` +
        `Add a conversion or fix upstream — the lenient form assumes degF, which is a ` +
        `silent fallback of exactly the kind that masked the density and viscosity bugs.`,
    );
  }
  return temperatureToK(value, unit);
}

const SPECIFIC_HEAT_KNOWN_UNITS = new Set([
  'btu/(lb·degf)', 'btu/(lb·°f)', 'btu/lb·degf', 'btu/lb·°f', 'btu/(lb-degf)', 'btu/(lb-°f)',
  'kj/(kg·k)', 'kj/(kg·°k)', 'kj/(kg-k)',
  'j/(kg·k)', 'j/(kg·°k)', 'j/(kg-k)', 'j/(kg.k)',
  'j/kg·k', 'j/kg-k', 'j/kg.k', 'j/kg/k', 'j/kgk',
  'm^2 s^-2 k^-1', // pipe-converter internal form
]);

/**
 * {@link specificHeatToJkgK}, refusing a unit it does not know.
 *
 * The lenient form multiplies ANY unrecognised unit by 4186.8. The Desktop
 * corpus spells Cp `m^2 s^-2 K^-1` — already J/(kg·K) — on 141 zones, so a
 * missing alias turned 4241 into 17.8 million and silently wrecked every
 * thermal calculation. That alias is now present; this exists so the next
 * missing one fails loudly instead.
 */
export function specificHeatToJkgKStrict(value: number, unit: string): number {
  if (!SPECIFIC_HEAT_KNOWN_UNITS.has(unit.toLowerCase().trim())) {
    throw new Error(
      `specificHeatToJkgKStrict: unsupported specific-heat unit "${unit}" (raw value ${value}). ` +
        `The lenient form would multiply by 4186.8 — a silent fallback of exactly the kind ` +
        `that masked the density and viscosity bugs.`,
    );
  }
  return specificHeatToJkgK(value, unit);
}

export function pressureToPaStrict(value: number, unit: string): number {
  if (!PRESSURE_TO_PA_KNOWN_UNITS.has(unit.toLowerCase().trim())) {
    throw new Error(
      `pressureToPaStrict: unsupported pressure unit "${unit}" (raw value ${value}). ` +
        `Add a conversion or fix upstream — silent fallbacks masked the 16× density / ` +
        `1000× viscosity bugs for months.`,
    );
  }
  return pressureToPa(value, unit);
}

/**
 * Density of a pure gas at standard reference conditions (ideal gas):
 *
 *   ρ_std = P_std · MW / (R · T_std)
 *
 * with MW in g/mol (kg/kmol), the form used throughout the fluid zones.
 *
 * Uses {@link P_STD_PA} / {@link T_STD_K} (101325 Pa / 60 °F), CONFIRMED
 * against ESI C++ (units/utility/conversion.hpp standard_reference_*).
 * Anchor: dry air (MW ≈ 28.96546) reproduces ESI's air_density_60F_kgpm3
 * = 1.22268707852639 kg/m³ to ~2e-5 (see the anchor test). The legacy
 * STANDARD_AIR_DENSITY_KGM3 = 1.2 (~20 °C) is superseded for gas work.
 */
export function standardGasDensityKgM3(molecularWeight: number): number {
  if (!(molecularWeight > 0)) {
    throw new Error(
      `standardGasDensityKgM3: molecularWeight must be > 0 (got ${molecularWeight})`,
    );
  }
  return (P_STD_PA * (molecularWeight / 1000)) / (R_UNIVERSAL_J_MOL_K * T_STD_K);
}

/**
 * Convert a pressure DELTA (e.g. pump head rise, control-valve dP setpoint)
 * to Pascals. Unlike {@link pressureToPa}, the gauge marker on the unit
 * (`psi g`, `bar g`, `kpa g`) is stripped — atmospheric pressure must NOT
 * be added to a delta, since the offset cancels in `P_out − P_in`.
 *
 * @throws on unrecognized units (see [[feedback_no_silent_fallbacks]]).
 */
export function pressureDeltaToPa(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'psi':
    case 'psia':
    case 'psig':
    case 'psi g':
      return value * PSI_TO_PA;
    case 'bar':
    case 'bara':
    case 'barg':
    case 'bar g':
      return value * BAR_TO_PA;
    case 'kpa':
    case 'kpaa':
    case 'kpag':
    case 'kpa g':
      return value * KPA_TO_PA;
    case 'pa':
    case 'paa':
    case 'pa g':
    case 'pag':
      // Delta frame: the gauge marker is stripped, so all Pa variants
      // pass through unchanged (kept in lockstep with pressureToPa's set).
      return value;
    case 'atm':
      return value * ATM_TO_PA;
    case 'inh2o':
    case 'in h2o':
    case 'inwc':
    case 'in wc':
    case 'inh2o a':
    case 'in h2o a':
    case 'inh2o g':
    case 'in h2o g':
      return value * IN_H2O_TO_PA;
    default:
      throw new Error(
        `pressureDeltaToPa: unsupported pressure unit "${unit}" (raw value ${value}). ` +
          `Add a conversion or fix upstream — silent fallbacks masked the 16× density / ` +
          `1000× viscosity bugs for months.`,
      );
  }
}

/**
 * Unit strings {@link pressureDeltaToPa} recognizes. Kept in lockstep with
 * its switch (same convention as {@link PRESSURE_TO_PA_KNOWN_UNITS} for
 * {@link pressureToPa}).
 */
const PRESSURE_DELTA_TO_PA_KNOWN_UNITS = new Set([
  'psi', 'psia', 'psig', 'psi g',
  'bar', 'bara', 'barg', 'bar g',
  'kpa', 'kpaa', 'kpag', 'kpa g',
  'pa', 'paa', 'pa g', 'pag',
  'atm',
  'inh2o', 'in h2o', 'inwc', 'in wc', 'inh2o a', 'in h2o a', 'inh2o g', 'in h2o g',
]);

/**
 * True when {@link pressureDeltaToPa} would accept `unit` rather than throw.
 * For a caller ingesting unvalidated text (e.g. an optional field parsed
 * from a document extraction) that must check BEFORE converting and omit
 * rather than convert-and-throw. Failing an entire import over one
 * unrecognized unit on an optional field nothing downstream reads yet
 * would be disproportionate; pressureDeltaToPa itself must keep throwing
 * for every other caller (PIPE-150).
 */
export function isKnownPressureDeltaUnit(unit: string): boolean {
  return PRESSURE_DELTA_TO_PA_KNOWN_UNITS.has(unit.toLowerCase().trim());
}

/**
 * Convert a volumetric flow rate to m³/s.
 * Defaults to gpm for unknown units.
 */
export function flowToM3s(value: number, unit: string): number {
  const u = unit.toLowerCase().trim();
  switch (u) {
    case 'gpm':
    case 'gal/min':
      return value * GPM_TO_M3S;
    case 'l/min':
    case 'lpm':
      return value * LPM_TO_M3S;
    case 'm³/s':
    case 'm3/s':
    case 'm^3/s':
      return value;
    case 'm³/h':
    case 'm3/h':
    case 'm^3/h':
      return value / 3600;
    case 'l/s':
      return value * LS_TO_M3S;
    case 'ft³/min':
    case 'ft3/min':
    case 'cfm':
    case 'scfm':
      // scfm IS a mass-flow unit dressed up as volumetric — pure
      // volumetric conversion is meaningless without density. Callers
      // that need scfm should use `flowToKgS` which routes scfm through
      // the standard density. We return the m³/s equivalent at standard
      // conditions here so misuse fails noisily rather than silently.
      return value * FT3_PER_MIN_TO_M3S;
    default:
      throw new Error(
        `flowToM3s: unsupported flow unit "${unit}" (raw value ${value}). ` +
          `Add a conversion or fix upstream — silent fallbacks (previously gpm) ` +
          `masked density/viscosity unit bugs for months.`,
      );
  }
}

/**
 * True when `unit` is a mass-flow unit that {@link massFlowToKgS} converts
 * WITHOUT needing a density. Kept in lock-step with that function's accepted
 * set so `isMassFlowUnit(u) ? massFlowToKgS(v, u) : …` never diverges.
 *
 * `scfm` is deliberately EXCLUDED: it is a standard-referenced pseudo-mass
 * unit whose conversion needs the gas standard density, so it is neither a
 * pure mass unit nor a pure volumetric one — callers must route it explicitly.
 */
export function isMassFlowUnit(unit: string): boolean {
  switch (unit.toLowerCase().trim()) {
    case 'kg/s':
    case 'kg/h':
    case 'lb/s':
    case 'lbs/s':
    case 'lb/h':
    case 'lbs/h':
    case 'lb/hr':
    case 'lbs/hr':
      return true;
    default:
      return false;
  }
}

/**
 * Convert a flow rate to mass flow (kg/s).
 *
 * Handles both volumetric and mass-flow units. For volumetric units the
 * fluid density is required. For `scfm` (Standard Cubic Feet per Minute),
 * the density of standard atmospheric air is used because scfm is defined
 * at standard reference conditions — using operating-state density would
 * over-count mass for high-pressure gas systems by the ratio
 * (ρ_operating / ρ_standard).
 *
 * @param value - Flow rate in the specified unit
 * @param unit - Flow unit string
 * @param density - Fluid density at operating conditions in kg/m³
 * @param standardDensity - Gas density at STANDARD conditions in kg/m³
 *   (from {@link standardGasDensityKgM3} via the zone's molecular_weight).
 *   REQUIRED for `scfm` — desktop converts scfm with the zone gas's
 *   standard density (101325 Pa / 60 °F), and the legacy air constant
 *   1.2 kg/m³ (~20 °C) under-read air mass flows by 1.85 % (the entire
 *   Compressed-air-system warn cluster) and would be ~3 % off for N2.
 */
export function flowToKgS(value: number, unit: string, density: number, standardDensity?: number): number {
  const u = unit.toLowerCase().trim();
  // Mass-flow units — density not needed.
  if (u === 'kg/s') return value;
  if (u === 'kg/h') return value / 3600;
  if (u === 'lb/s') return value * LBS_TO_KGS;
  if (u === 'lb/h') return (value * LBS_TO_KGS) / 3600;
  // scfm uses the gas STANDARD density, not operating density. No silent
  // air-constant fallback: callers must resolve the zone gas
  // (resolveScfmStandardDensityForVertex in the calc-engine, or
  // standardGasDensityKgM3 directly).
  if (u === 'scfm') {
    if (standardDensity === undefined) {
      throw new Error(
        `flowToKgS: scfm conversion requires the gas standard density ` +
          `(zone molecular_weight → standardGasDensityKgM3). The legacy ` +
          `1.2 kg/m³ air default was removed — it silently mis-read gas ` +
          `mass flows (no silent fallbacks).`,
      );
    }
    return value * FT3_PER_MIN_TO_M3S * standardDensity;
  }
  // sm³/s (Standard cubic metre per second) is scfm's SI sibling: a
  // standard-referenced pseudo-mass unit. The value is already in standard
  // m³/s, so no volumetric factor — just multiply by the gas standard density
  // (same 60 °F/101325 Pa basis PIPE-FLO uses for scfm; verified byte-exact
  // against the flow_out_std oracle: 1.857 sm³/s · 1.2224 = 2.26995 kg/s).
  if (u === 'sm³/s') {
    if (standardDensity === undefined) {
      throw new Error(
        `flowToKgS: sm³/s conversion requires the gas standard density ` +
          `(zone molecular_weight → standardGasDensityKgM3). No silent fallbacks.`,
      );
    }
    return value * standardDensity;
  }
  // Volumetric units — use operating-state density.
  return flowToM3s(value, unit) * density;
}

/**
 * Convert density to kg/m³.
 *
 * Throws on an unrecognized unit. The previous behavior — silently treating
 * unknown units as lbm/ft³ — masked import bugs where upstream parsers
 * supplied SI values without the matching unit string, then the conversion
 * factor (×16.018) was applied as if the value were imperial. The result was
 * densities that read as `kg/m³` in the JSON but were 16× too large.
 *
 * Add an alias if you legitimately encounter a new unit string in production
 * data. Do NOT add a silent fallback.
 */
export function densityToKgM3(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'lb/ft³':
    case 'lb/ft3':
    case 'lbm/ft3':
    case 'lbm/ft³':
    case 'lbm/ft^3':
    case 'lb/ft^3':
      return value * LBFT3_TO_KGM3;
    case 'kg/m³':
    case 'kg/m3':
    case 'kg/m^3':
    case 'kgm3':
    case 'kg.m-3':
    case 'm^-3 kg': // pipe-converter internal form
      return value;
    case 'g/cm3':
    case 'g/cm³':
    case 'g/cm^3':
      return value * GCM3_TO_KGM3;
    default:
      throw new Error(
        `densityToKgM3: unrecognized unit "${unit}". Add an alias to the switch ` +
        `if this is a legitimate unit string. Do not silently default — silent ` +
        `defaults masked the lbm/ft³ → kg/m³ double-conversion bug.`,
      );
  }
}

/**
 * Convert dynamic viscosity to Pa·s.
 *
 * Throws on an unrecognized unit. Same rationale as `densityToKgM3` — the
 * previous silent default applied ×0.001 (cP → Pa·s) to anything unknown,
 * which produced viscosities that read as `Pa·s` but were 1000× too small.
 */
export function viscosityToPaS(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'cp':
    case 'centipoise':
      return value * CP_TO_PAS;
    case 'pa·s':
    case 'pa*s':
    case 'pa.s':
    case 'pa_s': // fluid-database DIPPR (Type-2) correlation output unit
    case 'pas':
    case 'm^-1 kg s^-1': // pipe-converter internal form
      return value;
    case 'lbm/hr-ft':
    case 'lbm/hr·ft':
    case 'lb/hr-ft':
    case 'lb/hr·ft':
      // 1 lbm/hr-ft = 0.000413 Pa·s (1 lbm = 0.4536 kg, 1 hr = 3600 s, 1 ft = 0.3048 m)
      return value * 0.00041337887;
    case 'lbm/ft-s':
    case 'lbm/ft·s':
    case 'lb/ft-s':
    case 'lb/ft·s':
      // 1 lbm/ft-s = 1.4882 Pa·s
      return value * 1.48816394;
    default:
      throw new Error(
        `viscosityToPaS: unrecognized unit "${unit}". Add an alias to the switch ` +
        `if this is a legitimate unit string. Do not silently default — silent ` +
        `defaults masked the cP → Pa·s double-conversion bug.`,
      );
  }
}

// ============================================================================
// Display Conversions (SI → Imperial for UI)
// ============================================================================

/** Convert mass flow (kg/s) to gallons per minute. */
export function kgSToGpm(massFlowKgS: number, density: number): number {
  return (massFlowKgS / density) / GPM_TO_M3S;
}

/** Convert Pascals to psi. */
export function paToPsi(pa: number): number {
  return pa / PSI_TO_PA;
}

/** Convert meters to feet. */
export function metersToFt(m: number): number {
  return m / FT_TO_M;
}

// ============================================================================
// New toBase Functions
// ============================================================================

/** Convert temperature to Kelvin. Non-linear (offset-based). */
export function temperatureToK(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'degf':
    case '°f':
    case 'f':
      return (value - FAHRENHEIT_OFFSET) * 5 / 9 + CELSIUS_TO_KELVIN_OFFSET;
    case 'degc':
    case '°c':
    case 'c':
      return value + CELSIUS_TO_KELVIN_OFFSET;
    case 'k':
    case 'kelvin':
      return value;
    default:
      return (value - FAHRENHEIT_OFFSET) * 5 / 9 + CELSIUS_TO_KELVIN_OFFSET; // AFT default: degF
  }
}

/** Convert velocity to m/s. */
export function velocityToMs(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'ft/s':
    case 'fps':
      return value * FTS_TO_MS;
    case 'm/s':
      return value;
    default:
      return value * FTS_TO_MS; // AFT default: ft/s
  }
}

/**
 * Convert power to Watts.
 *
 * Throws on an unrecognized unit ([[feedback_no_silent_fallbacks]]). The
 * previous default silently converted unknown units as HORSEPOWER (×745.7):
 * the pipe-converter emits heat_transfer_rate in 'btu_it_per_hour'
 * (reverse-mapper.ts:1337), which the hp default would have inflated ~2500×.
 */
export function powerToW(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'hp':
      return value * HP_TO_W;
    case 'hp_elec':
      // Electric horsepower (pipe-converter pump power-curve unit id) — 746 W exact.
      return value * 746;
    case 'kw':
      return value * KW_TO_W;
    case 'w':
    case 'watt':
    case 'watts':
      return value;
    case 'btu/h':
    case 'btu/hr':
    case 'btu/hour':
    case 'btu_it_per_hour': // pipe-converter heat_transfer_rate unit
    case 'btuitph': // .pipe document heat_transfer_rate_unit display id
      return value * BTU_H_TO_W;
    default:
      throw new Error(
        `powerToW: unsupported power unit "${unit}" (raw value ${value}). ` +
          `Add a conversion or fix upstream — the previous silent hp default ` +
          `would have converted btu_it_per_hour as horsepower (~2500× wrong).`,
      );
  }
}

/** Convert specific heat to J/(kg·K). */
export function specificHeatToJkgK(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'btu/(lb·degf)':
    case 'btu/(lb·°f)':
    case 'btu/lb·degf':
    case 'btu/lb·°f':
    case 'btu/(lb-degf)':
    case 'btu/(lb-°f)':
      return value * BTU_LB_DEGF_TO_J_KG_K;
    case 'kj/(kg·k)':
    case 'kj/(kg·°k)':
    case 'kj/(kg-k)':
      return value * KJ_KG_K_TO_J_KG_K;
    // Every SI spelling of J/(kg·K). The parenthesis-free forms matter:
    // `FluidZoneSchema` documents `J/kg·K` and the thermal fixtures use
    // `J/kg-K`, so without them a schema-valid Model was multiplied by 4186.8
    // by the lenient form and REJECTED outright by the strict one. The last is
    // the pipe-converter's internal spelling, as in densityToKgM3.
    case 'j/(kg·k)':
    case 'j/(kg·°k)':
    case 'j/(kg-k)':
    case 'j/(kg.k)':
    case 'j/kg·k':
    case 'j/kg-k':
    case 'j/kg.k':
    case 'j/kg/k':
    case 'j/kgk':
    case 'm^2 s^-2 k^-1':
      return value;
    default:
      return value * BTU_LB_DEGF_TO_J_KG_K; // AFT default: BTU/(lb·degF)
  }
}

/** Convert head to meters. Same physics as lengthToMeters but semantically separate. */
export function headToM(value: number, unit: string): number {
  return lengthToMeters(value, unit);
}

/** Convert mass flow to kg/s. Throws on unknown units — see
 * [[feedback_no_silent_fallbacks]]. The previous default-to-lb/s
 * silently transformed `40 scfm` into `18.14 kg/s` (an 800× mass-flow
 * error) because the desktop transformer routed FlowDemand's `flow_in`/
 * `flow_out` modes through this helper regardless of unit. */
export function massFlowToKgS(value: number, unit: string): number {
  switch (unit.toLowerCase().trim()) {
    case 'kg/s':
      return value;
    case 'kg/h':
      return value / 3600;
    case 'lb/s':
    case 'lbs/s':
      return value * LBS_TO_KGS;
    case 'lb/h':
    case 'lbs/h':
    case 'lb/hr':
    case 'lbs/hr':
      return value * LBS_TO_KGS / 3600;
    default:
      throw new Error(
        `massFlowToKgS: unsupported mass-flow unit "${unit}" (raw value ${value}). ` +
          `If this is a volumetric or standard-flow unit (scfm/gpm/m³/s), use ` +
          `flowToKgS(value, unit, density) which handles both families.`,
      );
  }
}

// ============================================================================
// fromBase Functions (SI → Display)
// ============================================================================

/**
 * Convert absolute Pascals to a target pressure unit.
 *
 * Gauge vs. absolute semantics mirror {@link pressureToPa}:
 *   - `psi`, `psia`, `kpa`, `kpaa`, `bar`, `bara` → absolute (no offset)
 *   - `psig`, `kpag`, `barg` → gauge; atmospheric is SUBTRACTED
 */
export function paToUnit(pa: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'psi':
    case 'psia':
      return pa / PSI_TO_PA;
    case 'psig':
    case 'psi g':
      return (pa - ATM_TO_PA) / PSI_TO_PA;
    case 'kpa':
    case 'kpaa':
      return pa / KPA_TO_PA;
    case 'kpag':
    case 'kpa g':
      return (pa - ATM_TO_PA) / KPA_TO_PA;
    case 'bar':
    case 'bara':
      return pa / BAR_TO_PA;
    case 'barg':
    case 'bar g':
      return (pa - ATM_TO_PA) / BAR_TO_PA;
    case 'atm':
      return pa / ATM_TO_PA;
    case 'mmhg':
    case 'torr':
      return pa / MMHG_TO_PA;
    case 'inh2o':
    case 'in h2o':
    case 'inwc':
    case 'in wc':
    case 'inh2o a':
    case 'in h2o a':
      return pa / IN_H2O_TO_PA;
    case 'inh2o g':
    case 'in h2o g':
      return (pa - ATM_TO_PA) / IN_H2O_TO_PA;
    case 'pa':
      return pa;
    default:
      return pa / PSI_TO_PA;
  }
}

/**
 * Convert a pressure DELTA in Pascals to a target unit for display. The
 * reverse of {@link pressureDeltaToPa} and the delta-safe counterpart of
 * {@link paToUnit}: the gauge marker (`psi g`, `bar g`, `kpa g`) is stripped,
 * so atmospheric is NEVER subtracted. The offset cancels in `P_out − P_in`, so
 * applying it to a difference is what rendered a ~0 Pa pipe drop as ≈ −14.7 psi.
 *
 * Use for every Δp display: pipe pressure drop, friction/fittings loss, pump
 * head, control-valve dP. Use {@link paToUnit} for absolute node pressures.
 *
 * @throws on unrecognized units (see [[feedback_no_silent_fallbacks]]).
 */
export function paToDeltaUnit(pa: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'psi':
    case 'psia':
    case 'psig':
    case 'psi g':
      return pa / PSI_TO_PA;
    case 'bar':
    case 'bara':
    case 'barg':
    case 'bar g':
      return pa / BAR_TO_PA;
    case 'kpa':
    case 'kpaa':
    case 'kpag':
    case 'kpa g':
      return pa / KPA_TO_PA;
    case 'atm':
      return pa / ATM_TO_PA;
    case 'inh2o':
    case 'in h2o':
    case 'inwc':
    case 'in wc':
    case 'inh2o a':
    case 'in h2o a':
    case 'inh2o g':
    case 'in h2o g':
      return pa / IN_H2O_TO_PA;
    case 'pa':
      return pa;
    default:
      throw new Error(
        `paToDeltaUnit: unsupported pressure unit "${targetUnit}" (raw value ${pa}). ` +
          `Add a conversion or fix upstream — silent fallbacks masked the 16× density / ` +
          `1000× viscosity bugs for months.`,
      );
  }
}

/** Convert meters to a target length unit. */
export function mToUnit(m: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'ft':
    case 'feet':
    case 'foot':
      return m / FT_TO_M;
    case 'in':
    case 'inch':
    case 'inches':
      return m / IN_TO_M;
    case 'mm':
    case 'millimeter':
    case 'millimeters':
      return m / MM_TO_M;
    case 'cm':
    case 'centimeter':
    case 'centimeters':
      return m / CM_TO_M;
    case 'm':
    case 'meter':
    case 'meters':
      return m;
    default:
      return m / FT_TO_M;
  }
}

/** Convert m³/s to a target volumetric flow unit. */
export function m3sToUnit(m3s: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'gpm':
    case 'gal/min':
      return m3s / GPM_TO_M3S;
    case 'l/min':
    case 'lpm':
      return m3s / LPM_TO_M3S;
    case 'l/s':
      return m3s / LS_TO_M3S;
    case 'm³/h':
    case 'm3/h':
      return m3s * 3600;
    case 'm³/s':
    case 'm3/s':
      return m3s;
    default:
      return m3s / GPM_TO_M3S;
  }
}

/** Convert kg/m³ to a target density unit. */
export function kgm3ToUnit(kgm3: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'lb/ft³':
    case 'lb/ft3':
    case 'lbm/ft3':
    case 'lbm/ft³':
      return kgm3 / LBFT3_TO_KGM3;
    case 'g/cm3':
    case 'g/cm³':
      return kgm3 / GCM3_TO_KGM3;
    case 'kg/m³':
    case 'kg/m3':
      return kgm3;
    default:
      return kgm3 / LBFT3_TO_KGM3;
  }
}

/** Convert Pa·s to a target viscosity unit. */
export function pasToUnit(pas: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'cp':
    case 'centipoise':
      return pas / CP_TO_PAS;
    case 'pa·s':
    case 'pa*s':
    case 'pas':
      return pas;
    default:
      return pas / CP_TO_PAS;
  }
}

/** Convert Kelvin to a target temperature unit. Non-linear (offset-based). */
export function kToUnit(k: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'degf':
    case '°f':
    case 'f':
      return (k - CELSIUS_TO_KELVIN_OFFSET) * 9 / 5 + FAHRENHEIT_OFFSET;
    case 'degc':
    case '°c':
    case 'c':
      return k - CELSIUS_TO_KELVIN_OFFSET;
    case 'k':
    case 'kelvin':
      return k;
    default:
      return (k - CELSIUS_TO_KELVIN_OFFSET) * 9 / 5 + FAHRENHEIT_OFFSET;
  }
}

/** Convert m/s to a target velocity unit. */
export function msToUnit(ms: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'ft/s':
    case 'fps':
      return ms / FTS_TO_MS;
    case 'm/s':
      return ms;
    default:
      return ms / FTS_TO_MS;
  }
}

/** Convert Watts to a target power unit. */
export function wToUnit(w: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'hp':
      return w / HP_TO_W;
    case 'kw':
      return w / KW_TO_W;
    case 'w':
    case 'watt':
    case 'watts':
      return w;
    case 'btu/h':
    case 'btu/hr':
    case 'btu/hour':
      return w / BTU_H_TO_W;
    default:
      return w / HP_TO_W;
  }
}

/** Convert J/(kg·K) to a target specific heat unit. */
export function jkgkToUnit(j: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'btu/(lb·degf)':
    case 'btu/(lb·°f)':
    case 'btu/lb·degf':
    case 'btu/lb·°f':
    case 'btu/(lb-degf)':
    case 'btu/(lb-°f)':
      return j / BTU_LB_DEGF_TO_J_KG_K;
    case 'kj/(kg·k)':
    case 'kj/(kg·°k)':
    case 'kj/(kg-k)':
      return j / KJ_KG_K_TO_J_KG_K;
    case 'j/(kg·k)':
    case 'j/(kg·°k)':
    case 'j/(kg-k)':
      return j;
    default:
      return j / BTU_LB_DEGF_TO_J_KG_K;
  }
}

/** Convert kg/s to a target mass flow unit. */
export function kgsToUnit(kgs: number, targetUnit: string): number {
  switch (targetUnit.toLowerCase().trim()) {
    case 'lb/s':
    case 'lbs/s':
      return kgs / LBS_TO_KGS;
    case 'kg/s':
      return kgs;
    default:
      return kgs / LBS_TO_KGS;
  }
}
